import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faClock, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import classnames from 'classnames';
import { isFuture } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useProbabilisticBundleConfigSummary } from '@sorare/core/src/hooks/useProbabilisticBundleConfigSummary';
import { useProbableRewardConfigSummary } from '@sorare/core/src/hooks/useProbableRewardConfigSummary';
import { glossary } from '@sorare/core/src/lib/glossary';
import { mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import type {
  PromoBanner_ProbabilisticBundleConfig,
  PromoBanner_ProbableRewardConfig,
  PromoBanner_PromotionalEvent,
  PromoBanners_PromotionalEventsInterface,
} from './__generated__/index.graphql';

const PromoBannersWrapper = styled(Vertical).attrs({ gap: 1 })`
  width: 100%;
`;

const Root = styled(Horizontal).attrs({ center: true, gap: 1 })`
  width: 100%;
  color: var(--c-black);
  padding: 2px 4px;

  background: linear-gradient(
    84.1deg,
    #f8d3da 0%,
    #b3a9f4 28.32%,
    #fbe9fb 54.01%,
    #4f94fd 100%
  );

  &.rounded {
    border-radius: var(--unit);
  }

  @media ${mobileAndAbove} {
    display: grid;
    grid-template-columns: 40px 1fr 40px;
    grid-template-areas: 'gutter content button';
  }
  min-height: var(--quadruple-unit);
`;

const Content = styled(Horizontal).attrs({ center: true, gap: 1 })`
  grid-area: content;
`;

const ButtonContainer = styled.div`
  color: var(--c-white);
  grid-area: button;
`;

const Text = styled(Horizontal).attrs({ gap: 0 })`
  flex-wrap: wrap;
  column-gap: var(--unit);
`;

const Terms = styled(LabelS)`
  text-decoration: underline;
  color: inherit;
`;

type PromoBannerProps = {
  rounded?: boolean;
  count?: number;
  promotionalEvent: PromoBanner_PromotionalEvent;
  probableRewardConfigs: PromoBanner_ProbableRewardConfig[];
  probabilisticBundleConfig?: PromoBanner_ProbabilisticBundleConfig;
};

const PromoBanner = ({
  rounded,
  count = 1,
  promotionalEvent,
  probableRewardConfigs,
  probabilisticBundleConfig,
}: PromoBannerProps) => {
  const { termsAndConditionsUrl, blogUrl, endDate } = promotionalEvent;
  const probabilisticBundleConfigSummary = useProbabilisticBundleConfigSummary(
    probabilisticBundleConfig
  );

  const probableRewardsSummary = useProbableRewardConfigSummary(
    probableRewardConfigs
  );

  const { locale } = useIntl();
  const AndList = new Intl.ListFormat(locale, {
    style: 'long',
    type: 'conjunction',
  });
  const OrList = new Intl.ListFormat(locale, {
    style: 'long',
    type: 'disjunction',
  });

  const dedupe = (list: string[]) => {
    return [...new Set(list)];
  };

  const rewards = probabilisticBundleConfigSummary.length
    ? OrList.format(
        dedupe(probabilisticBundleConfigSummary.map(reward => reward.name))
      )
    : OrList.format(
        dedupe(
          probableRewardsSummary.map(rewardSet =>
            AndList.format(dedupe(rewardSet.map(reward => reward.name)))
          )
        )
      );

  return (
    <Root className={classnames({ rounded })}>
      <Content>
        {!!endDate && isFuture(endDate) && (
          <Chip color="grey">
            <Horizontal gap={0.5}>
              <FontAwesomeIcon icon={faClock} />
              <TimeLeft time={endDate} />
            </Horizontal>
          </Chip>
        )}
        <LabelM bold>{count > 1 && `${count}x `}</LabelM>
        {!!probabilisticBundleConfig && (
          <ScarcityIcon size={3} rarity={probabilisticBundleConfig.rarity} />
        )}
        <Text>
          <LabelM bold>
            {rewards ? (
              <FormattedMessage
                id="PromoBanner.message"
                defaultMessage="Win {rewards} when buying"
                values={{
                  rewards,
                }}
              />
            ) : (
              <FormattedMessage
                id="PromoBanner.message.default"
                defaultMessage="Win rewards when buying"
              />
            )}
          </LabelM>

          {!!termsAndConditionsUrl && (
            <Terms
              rel="noopener noreferrer"
              target="_blank"
              href={termsAndConditionsUrl}
              as="a"
            >
              (<FormattedMessage {...glossary.termsApply} />)
            </Terms>
          )}
        </Text>
      </Content>
      {!!blogUrl && (
        <ButtonContainer>
          <IconButton
            href={blogUrl}
            externalLink
            size="small"
            icon={faInfoCircle}
            color="transparent"
          />
        </ButtonContainer>
      )}
    </Root>
  );
};

PromoBanner.fragments = {
  PromotionalEvent: gql`
    fragment PromoBanner_PromotionalEvent on PromotionalEventInterface {
      slug
      blogUrl
      termsAndConditionsUrl
      endDate
    }
    ${useProbableRewardConfigSummary.fragments.ProbableRewardConfig}
  ` as TypedDocumentNode<PromoBanner_PromotionalEvent>,
  ProbabilisticBundleConfig: gql`
    fragment PromoBanner_ProbabilisticBundleConfig on ProbabilisticBundleConfig {
      id
      rarity
      ...useProbabilisticBundleConfigSummary_ProbabilisticBundleConfig
    }
    ${useProbabilisticBundleConfigSummary.fragments.ProbabilisticBundleConfig}
  ` as TypedDocumentNode<PromoBanner_ProbabilisticBundleConfig>,
  ProbableRewardConfig: gql`
    fragment PromoBanner_ProbableRewardConfig on ProbableRewardConfig {
      id
      ...useProbableRewardConfigSummary_ProbableRewardConfig
    }
    ${useProbableRewardConfigSummary.fragments.ProbableRewardConfig}
  ` as TypedDocumentNode<PromoBanner_ProbableRewardConfig>,
};

type Props = {
  rounded?: boolean;
  promotionalEvents: PromoBanners_PromotionalEventsInterface;
};

export const PromoBanners = ({ rounded, promotionalEvents }: Props) => {
  const { wheel } = useConfigContext();
  const nbTicketsRewarded =
    wheel.limitedWheelConfig?.ticketsRewardedAfterPurchase.amount ?? 0;

  const groupedPromotions = promotionalEvents.eligiblePromotionalEvents.reduce<
    Record<
      string,
      {
        count: number;
        promotionalEvent: PromoBanners_PromotionalEventsInterface['eligiblePromotionalEvents'][number];
        reward: PromoBanners_PromotionalEventsInterface['eligibleRewardsProbabilisticBundleConfigs'][number];
      }
    >
  >((acc, promotionalEvent, index) => {
    if (!acc[promotionalEvent.slug]) {
      acc[promotionalEvent.slug] = {
        count: 1,
        promotionalEvent,
        reward:
          promotionalEvents.eligibleRewardsProbabilisticBundleConfigs[index],
      };
    } else {
      acc[promotionalEvent.slug].count += 1;
    }
    return acc;
  }, {});

  if (Object.keys(groupedPromotions).length === 0 && nbTicketsRewarded === 0) {
    return null;
  }

  return (
    <PromoBannersWrapper>
      {Object.entries(groupedPromotions).map(
        ([slug, { count, promotionalEvent, reward }]) => (
          <PromoBanner
            key={slug}
            count={count}
            rounded={rounded}
            promotionalEvent={promotionalEvent}
            probabilisticBundleConfig={reward}
            probableRewardConfigs={
              promotionalEvents.eligiblePromotionalEventsProbableRewards
            }
          />
        )
      )}
      {nbTicketsRewarded > 0 && (
        <Root className={classnames({ rounded })}>
          <Content>
            <Text>
              <LabelM bold>
                <FormattedMessage
                  id="PromoBanner.message.tickets"
                  defaultMessage="Win {nb,plural, one {# ticket} other {# tickets}} when buying"
                  values={{
                    nb: nbTicketsRewarded,
                  }}
                />
              </LabelM>
            </Text>
          </Content>
        </Root>
      )}
    </PromoBannersWrapper>
  );
};

PromoBanners.fragments = {
  PromotionalEventsInterface: gql`
    fragment PromoBanners_PromotionalEventsInterface on PromotionalEventsInterface {
      eligiblePromotionalEvents {
        slug
        ...PromoBanner_PromotionalEvent
      }
      eligiblePromotionalEventsProbableRewards {
        id
        ...PromoBanner_ProbableRewardConfig
      }
      eligibleRewardsProbabilisticBundleConfigs {
        id
        ...PromoBanner_ProbabilisticBundleConfig
      }
    }
    ${PromoBanner.fragments.PromotionalEvent}
    ${PromoBanner.fragments.ProbabilisticBundleConfig}
    ${PromoBanner.fragments.ProbableRewardConfig}
  ` as TypedDocumentNode<PromoBanners_PromotionalEventsInterface>,
};
